import { WinType } from '@daymade/common-lib/dbEnums';
import { ZPromoConditions } from '@daymade/common-lib/dbJsonModels';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZPrizeCore, ZProductSelect } from '../prize';
import { ZPrizeOrderInsert } from '../winner/models';
import {
  ZPromoCodeRecurringSelect,
  ZPromoCodeSelect,
  ZPromoPrizeRewardSelect,
} from './models';

const ZUnclaimedPromoPrizeReward = ZPromoPrizeRewardSelect.pick({
  id: true,
  code: true,
  createdAt: true,
  expiresAt: true,
  status: true,
}).extend({
  prizeSlug: z.string().nullable(),
  prizeTitle: z.string().nullable(),
  prizeSubheading: z.string().nullable(),
  prizeImageLocation: z.string().nullable(),
  prizeTierCode: z.string().nullable(),
});

export type TUnclaimedPromoPrizeReward = z.infer<
  typeof ZUnclaimedPromoPrizeReward
>;

const ZClaimedPromoPrizeReward = ZUnclaimedPromoPrizeReward.extend({
  chosenPrizeProducts: ZProductSelect.pick({
    id: true,
    name: true,
    autoFulfilmentCode: true,
  })
    .extend({ prizeOrderProductId: z.string() })
    .array(),
});

export type TClaimedPromoPrizeReward = z.infer<typeof ZClaimedPromoPrizeReward>;

const c = initContract();
export const authPromoContract = c.router(
  {
    getUpgradeOffer: {
      method: 'GET',
      path: '/upgrade-offer',
      query: z.object({
        planCode: z.optional(z.string()).nullable(),
      }),
      responses: {
        200: z.object({
          promoCode: z.optional(ZPromoCodeSelect),
        }),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
      summary: 'Get prize wins of a draw',
    },

    getCancellationOffer: {
      method: 'GET',
      path: '/cancellation-offer',
      query: z.object({
        planCode: z.optional(z.string()).nullable(),
      }),
      responses: {
        200: z.object({
          promoCode: z.optional(ZPromoCodeSelect),
        }),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
      summary: 'Get cancellation offer promo code',
    },

    activatePromoCodeForSubscription: {
      method: 'POST',
      path: '/activate-promo-code',
      body: z.object({
        subscriptionId: z.string(),
        code: z.string(),
        evalData: z.optional(ZPromoConditions.partial()),
      }),
      responses: {
        200: z.object({
          promoCodeRecurring: ZPromoCodeRecurringSelect,
        }),
        400: c.type<{ message: string }>(),
      },
      summary: 'Activate a promo code for a subscription',
    },

    getPromoPrizeRewardsOfPlayer: {
      method: 'GET',
      path: '/promo-prize-rewards',
      responses: {
        200: z.object({
          unclaimed: ZUnclaimedPromoPrizeReward.array(),
          claimed: ZClaimedPromoPrizeReward.array(),
        }),
      },
      summary: 'Get promo prize rewards of a player',
    },

    getAvailablePrizesForRedemption: {
      method: 'GET',
      path: '/promo-prize-reward/:id/available-prizes',
      pathParams: z.object({
        id: z.string(),
      }),
      responses: {
        200: z.object({
          displayGroupPrizes: z.record(z.string(), ZPrizeCore.array()),
          allAvailablePrizes: ZPrizeCore.array(),
          winType: z.nativeEnum(WinType),
        }),
        400: z.object({
          message: z.string(),
        }),
      },
      summary: 'Get available prizes of a promo prize reward',
    },

    claimPromoPrizeReward: {
      method: 'POST',
      path: '/promo-prize-reward/:id/claim',
      pathParams: z.object({
        id: z.string(),
      }),
      body: z
        .object({
          prizeId: z.coerce.number(),
          productId: z.coerce.number().optional(),
        })
        .merge(
          ZPrizeOrderInsert.pick({
            deliveryStreet: true,
            deliveryCity: true,
            deliveryPostcode: true,
            emailAddress: true,
            phoneNumber: true,
            additionalInfoFromPlayer: true,
          }),
        ),
      responses: {
        200: z.object({
          prizeOrderId: z.string(),
          prizeOrderProductIds: z.string().array(),
        }),
        400: c.type<{ message: string }>(),
      },
      summary: 'Claim a promo prize reward',
    },
  },
  {
    pathPrefix: '/auth',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
